import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { putUser } from "store/thunks";
import * as Yup from "yup";
import { useRole } from "components/Hooks/UserHooks";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import DatePicker from "react-datepicker";
import { formatAddressByNaver, formatDateStringToKorea } from "helpers/format";
import SearchAddress from "components/Common/SearchAddress";
import addressApi from "api/addressApi";
import UploadFileBK from "components/Common/UploadFileBK";
import storeApi from "api/storeApi";

registerLocale("en", en);
registerLocale("ko", ko);

const BusinessInfoTab = ({ data, handleUpdateSuccess, setLinkImage }: any) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isOpenSearchAddress, setIsOpenSearchAddress] =
    useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    const isCheckBusinessNumber = await handleCheckBusinessNumber();
    console.log(isCheckBusinessNumber);
    if(!isCheckBusinessNumber) return;

    try {
      setIsLoading((_prev) => true);

      const formatValues = {
        ...values,
      };
      const response: any = await putUser(data?.id, {
        bussinessInfo: formatValues,
      });

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        handleUpdateSuccess && handleUpdateSuccess();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      // toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      toast(t("Invalid input"), CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    files: Yup.array()
      .of(
        Yup.object({
          url: Yup.string().nullable(),
        }).required(`${t("This field is required")}`)
      )
      .required(`${t("This field is required")}`)
      .min(1, "This field is required")
      .max(5, t("You can only select up to 5 images")),
    name: Yup.string()
      .trim()
      .required(`${t("This field is required")}`),
    businessType: Yup.string().required(`${t("This field is required")}`),
    productCategory: Yup.string().required(`${t("This field is required")}`),
    representativeName: Yup.string().required(`${t("This field is required")}`),
    businessNumber: Yup.string().required(`${t("This field is required")}`),
    address: Yup.string().required(`${t("This field is required")}`),
    establishedDate: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      businessNumber: "",
      establishedDate: new Date() || "",
      representativeName: "",
      address: "",
      shortAddress: "",
      region1: "",
      region2: "",
      region3: "",
      files: "",
      businessType: "",
      productCategory: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    const formatFields =
      valueDefault?.fields?.length > 0
        ? valueDefault?.fields?.map((item: any) => ({
            value: item?.id,
            label: (
              <div className="d-flex align-items-center gap-2">
                <div>{`${item?.title}`}</div>
              </div>
            ),
            data: item,
          }))
        : null;

    formik.setValues({
      ...valueDefault,
      address: {
        ...valueDefault?.address,
        region1: valueDefault?.address?.region_1,
        region2: valueDefault?.address?.region_2,
        region3: valueDefault?.address?.region_3,
        postal_code: String(valueDefault?.address?.postal_code),
      },
      fields: formatFields,
      logo_url: valueDefault?.logo_url
        ? [
            {
              fullPath: valueDefault?.logo_url,
            },
          ]
        : [],
      socials: valueDefault?.socials?.[0]?.sns
        ? [
            {
              url: valueDefault?.socials?.[0]?.url || "",
              sns: valueDefault?.socials?.[0]?.sns || "",
            },
          ]
        : null,
    });
  };

  const handleCheckBusinessNumber = async () => {
    formik.setFieldTouched("businessNumber", true, true);
    if (!formik?.values?.businessNumber) return;

    try {
      const response: any = await storeApi.verifyBusinessNumber({
        businessNumber: formik?.values?.businessNumber,
      });
      
      if (!response?.data?.isValid) {
        formik.setErrors({
          ...formik.errors,
          businessNumber: t("Invalid business number"),
        });
        return false;
      } else {
        const { businessNumber, ...restErrors } = formik.errors;
        formik.setErrors(restErrors);
        return true;
      }
    } catch (error) {
      formik.setErrors({
        ...formik.errors,
        businessNumber: t("Failed to verify business number"),
      });
      return false;
    }
  };

  const handleOpenSearchAddress = () => {
    setIsOpenSearchAddress(true);
  };

  const handleCloseModalAddress = () => {
    setIsOpenSearchAddress(false);
  };

  const getLatLngByAddress = async (address: string) => {
    try {
      if (address) {
        const data: any = await addressApi.getGEOByAddress({
          query: encodeURIComponent(address),
        });
        let dataAddress = data?.data?.addresses;
        const formatAddress = formatAddressByNaver(dataAddress);

        if (formatAddress) {
          formik?.setFieldValue("address", formatAddress?.address || "");
          formik?.setFieldValue(
            "shortAddress",
            formatAddress?.short_address || ""
          );
          formik?.setFieldValue("region1", formatAddress?.region1 || "");
          formik?.setFieldValue("region2", formatAddress?.region2 || "");
          formik?.setFieldValue("region3", formatAddress?.region3 || "");
        }
      }
    } catch (error) {
      //
      console.log(error);
    }
  };

  const onSetValueAddress = async (data: any) => {
    if (data) {
      const res = await getLatLngByAddress(data);
    }
    setIsOpenSearchAddress(false);
  };

  useEffect(() => {
    if (data) {
      const dataStore = data?.storeBusiness;
      formik.setValues({
        ...dataStore,
        name: dataStore?.name,
        description: dataStore?.description,
        address: dataStore?.address?.address,
        shortAddress: dataStore?.address?.shortAddress,
        region1: dataStore?.address?.region1,
        region2: dataStore?.address?.region2,
        region3: dataStore?.address?.region3,
        managerName: dataStore?.managerName,
        businessNumber: dataStore?.businessNumber,
        managerGender: dataStore?.managerGender,
        businessType: dataStore?.businessType,
        productCategory: dataStore?.productCategory,
        establishedDate: dataStore?.establishedDate
          ? new Date(formatDateStringToKorea(dataStore?.establishedDate))
          : null,
      });
    }
  }, [data]);

  useEffect(() => {
    document.title = `${t("Business Info")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  // console.log(formik.errors)
  return (
    <React.Fragment>
      <Row>
        <Col lg={12} className="mt-4 px-4">
          <form onSubmit={formik.handleSubmit}>
            <div
              className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
              style={{
                border: "1px dashed var(--vz-primary)",
                borderRadius: "4px",
              }}
            >
              <span
                className="text-primary position-absolute fs-13 px-1"
                style={{
                  backgroundColor: "var(--vz-secondary-bg)",
                  top: "0px",
                  transform: "translateY(-50%)",
                }}
              >
                {t("Working Conditions")}
              </span>
              <Row>
                <Col md={12} lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="name" className="form-label">
                      {t("Business Name")}{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="name"
                      type="text"
                      className="form-control"
                      id="name"
                      placeholder={t("Business Name")}
                      value={formik?.values?.name}
                      onChange={(event: any) =>
                        formik.setFieldValue("name", event?.target?.value)
                      }
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <div className="text-danger mt-2">
                        {formik.errors.name}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={12} lg={4}>
                  <div className="mb-3">
                    <Label htmlFor="businessNumber" className="form-label">
                      {t("Business Registration Number")}
                      <span className="text-danger"> *</span>
                    </Label>

                    <div className="d-flex mb-2 gap-2">
                      <Input
                        name="businessNumber"
                        type="text"
                        className="form-control"
                        id="businessNumber"
                        placeholder={t("Business Registration Number")}
                        value={formik?.values?.businessNumber}
                        onBlur={handleCheckBusinessNumber}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "businessNumber",
                            event?.target?.value
                          )
                        }
                      />
                      <button
                        type="button"
                        className="btn btn-primary whitespace-nowrap"
                        onClick={handleCheckBusinessNumber}
                      >
                        {t("Check")}
                      </button>
                    </div>

                    {formik.touched.businessNumber &&
                    formik.errors.businessNumber ? (
                      <div className="text-danger mt-2">
                        {formik.errors.businessNumber}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6} lg={4}>
                  <div className="mb-3">
                    <Label htmlFor="representativeName" className="form-label">
                      {t("Representative Name")}{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="representativeName"
                      type="text"
                      className="form-control"
                      id="representativeName"
                      placeholder={t("Representative Name")}
                      value={formik?.values?.representativeName}
                      onChange={(event: any) =>
                        formik.setFieldValue(
                          "representativeName",
                          event?.target?.value
                        )
                      }
                    />
                    {formik.touched.representativeName &&
                    formik.errors.representativeName ? (
                      <div className="text-danger mt-2">
                        {formik.errors.representativeName}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={12} lg={4}>
                  <div className="mb-3">
                    <Label htmlFor="establishedDate" className="form-label">
                      {t("Open Date")}
                      <span className="text-danger">*</span>
                    </Label>
                    <div className="w-100">
                      <DatePicker
                        name={"establishedDate"}
                        className="form-control w-100"
                        selected={formik?.values?.establishedDate}
                        placeholderText={t("Open Date")}
                        onChange={(date: any) => {
                          formik.setFieldValue("establishedDate", date);
                        }}
                        dateFormat="yyyy-MM-dd"
                        isClearable={true}
                      />
                    </div>
                    {formik.touched.establishedDate &&
                    formik.errors.establishedDate ? (
                      <div className="text-danger mt-2">
                        {formik.errors.establishedDate as String}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6} lg={6}></Col>

                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="address" className="form-label">
                      {t("Address")}
                      <span className="text-danger"> *</span>
                    </Label>

                    <div className="d-flex mb-2 gap-2">
                      <Input
                        name="address"
                        type="text"
                        className="form-control"
                        id="address"
                        placeholder={t("Address")}
                        value={formik?.values?.address}
                        onChange={(event: any) =>
                          formik.setFieldValue("address", event?.target?.value)
                        }
                      />
                      <button
                        type="button"
                        className="btn btn-primary whitespace-nowrap"
                        onClick={handleOpenSearchAddress}
                      >
                        {t("Find Address")}
                      </button>
                    </div>

                    {formik.touched.address && formik.errors.address ? (
                      <div className="text-danger mt-2">
                        {formik.errors.address}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="shortAddress" className="form-label">
                      {t("Short Address")}
                    </Label>
                    <Input
                      name="shortAddress"
                      type="text"
                      className="form-control"
                      id="shortAddress"
                      placeholder={t("Short Address")}
                      value={formik?.values?.shortAddress}
                      onChange={(event: any) =>
                        formik.setFieldValue(
                          "shortAddress",
                          event?.target?.value
                        )
                      }
                    />
                    {formik.touched.shortAddress &&
                    formik.errors.shortAddress ? (
                      <div className="text-danger mt-2">
                        {formik.errors.shortAddress}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="businessType" className="form-label">
                      {t("Business Type")}
                      <span className="text-danger"> *</span>
                    </Label>
                    <Input
                      name="businessType"
                      type="text"
                      className="form-control"
                      id="businessType"
                      placeholder={t("Business Type")}
                      value={formik?.values?.businessType}
                      onChange={(event: any) =>
                        formik.setFieldValue(
                          "businessType",
                          event?.target?.value
                        )
                      }
                    />
                    
                    {formik.touched.businessType &&
                    formik.errors.businessType ? (
                      <div className="text-danger mt-2">
                        {formik.errors.businessType}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="productCategory" className="form-label">
                      {t("Product Category")}
                      <span className="text-danger"> *</span>
                    </Label>
                    <Input
                      name="productCategory"
                      type="text"
                      className="form-control"
                      id="productCategory"
                      placeholder={t("Product Category")}
                      value={formik?.values?.productCategory}
                      onChange={(event: any) =>
                        formik.setFieldValue(
                          "productCategory",
                          event?.target?.value
                        )
                      }
                    />
                    {/* <DropdownOptionNew
                      name="productCategory"
                      dataList={[]}
                      placeholder={`${t("Product Category")}...`}
                      className="dropdown-status-rounded"
                      classNamePrefix="name-prefix"
                      initialValue={formik?.values?.productCategory}
                      onChangeSelect={(e: any) =>
                        formik.setFieldValue("productCategory", e?.value)
                      }
                      isHasOptionAll={false}
                    /> */}
                    {formik.touched.productCategory &&
                    formik.errors.productCategory ? (
                      <div className="text-danger mt-2">
                        {formik.errors.productCategory}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col xxl={12}>
                  <div className="mb-3">
                    <label className="form-label">
                      {t("Business Registration Certificate")}
                      <span className="text-danger"> *</span> <br />
                      <small>
                        {t(
                          "Upload up to 100MB in jpg, png image file format. Maximize 5 file"
                        )}
                      </small>
                    </label>

                    <UploadFileBK
                      formik={formik}
                      fieldValue="files"
                      isMultiple={true}
                      isUploadMore={
                        (formik?.values?.files && (formik?.values?.files as any)?.length >= 5 ? false : true) || (!formik?.values?.files)
                      }
                      // isUploadMore={
                      //   !formik?.values?.files ||
                      //   !(formik?.values?.files as any)?.length
                      // }
                      setLinkImage={setLinkImage}
                    />

                    {formik.touched.files && formik.errors.files ? (
                      <div className="text-danger mt-2">
                        {formik.errors.files}
                      </div>
                    ) : null}

                    {/* <UploadImage
                      data={formik.getFieldProps("files")?.value}
                      //setLinkImage={setLinkImage}
                      formik={formik}
                      isMultiple={false}
                      formikNameImage="files"
                    />

                    {formik.errors.files ? (
                      <div className="text-danger mt-2">
                        {formik.errors.files}
                      </div>
                    ) : null} */}
                  </div>
                </Col>
              </Row>
            </div>

            <Row className="mb-3 mt-2">
              <Col lg={12}>
                <div className="hstack gap-2 justify-content-end">
                  {isHavePermissionRole(
                    ROLES_FOR_APP.USER_UPDATE,
                    userPermissions
                  ) && (
                    <button type="submit" className="btn btn-primary fs-14"
                      disabled={Object.entries(formik?.errors)?.length > 0 ? true : false}
                    >
                      {isLoading && (
                        <Spinner size="sm" className="me-2"></Spinner>
                      )}
                      {t("Button Save")}
                    </button>
                  )}
                </div>
              </Col>
            </Row>
          </form>
        </Col>
      </Row>
      {isOpenSearchAddress && (
        <Modal
          isOpen={!!isOpenSearchAddress}
          centered={true}
          size="lg"
          scrollable={false}
        >
          <ModalHeader toggle={handleCloseModalAddress}>
            {t("Search Address")}
          </ModalHeader>
          <ModalBody>
            <SearchAddress onSetValueAddress={onSetValueAddress} />
          </ModalBody>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default BusinessInfoTab;
